import React, { useState, useMemo } from 'react'
import config from '../../config.js'
import { checkUploadSize } from '../../utils/uploadLimits'

const API = config.API_BASE_URL
const NESTER = config.NESTER_API_URL

const DENSITY_LBS = {
  'mild steel a36': 0.2836,
  'stainless steel': 0.2890,
  'aluminum': 0.0975,
  'copper': 0.3230,
  'brass': 0.3070,
}
const DEFAULT_DENSITY = 0.2836
const LEAD_IN_OUT = 0.1
const ACCEL_FACTOR = 0.93

function getDensity(materialType) {
  if (!materialType) return DEFAULT_DENSITY
  const key = materialType.toLowerCase()
  return DENSITY_LBS[key] || DEFAULT_DENSITY
}

function calcWeight(partAreaSqIn, thicknessIn, materialType) {
  if (!partAreaSqIn || !thicknessIn) return null
  return partAreaSqIn * thicknessIn * getDensity(materialType)
}

// Bare beam-on per-part time (Cut Path + Pierce dwell only). Used as a
// fallback when no nest result is available — sums of these will undershoot
// the full nest time. Prefer nest result's est_process_time_sec, which
// includes proportionally-allocated sheet overhead.
function calcBeamOnMinutes(cutLengthIn, pierceCount, feedrateIpm, pierceTimeSec) {
  if (!cutLengthIn || !feedrateIpm) return null
  const pc = pierceCount || 0
  const effectiveLength = cutLengthIn + pc * LEAD_IN_OUT
  const cutTimeSec = (effectiveLength / (feedrateIpm * ACCEL_FACTOR)) * 60
  const pierceTotalSec = pc * (pierceTimeSec || 0)
  return (cutTimeSec + pierceTotalSec) / 60
}

// Look up a part's full allocated time (and beam-on) from the nest result's
// parts_summary. Returns { fullMin, beamMin } in minutes, or null if not found.
// The nester's parts_summary entries cover ALL placed parts in the project's
// last nest run; for multi-group projects, every group result has its own
// parts_summary.
function lookupNesterTimes(partId, nestResult) {
  if (!nestResult || partId == null) return null
  const collect = (summary) => {
    if (!Array.isArray(summary)) return null
    return summary.find(p => p.part_id === partId) || null
  }
  let entry = collect(nestResult.parts_summary)
  if (!entry && Array.isArray(nestResult.group_results)) {
    for (const g of nestResult.group_results) {
      entry = collect(g.parts_summary)
      if (entry) break
    }
  }
  if (!entry) return null
  const fullSec = entry.est_process_time_sec || 0
  const beamSec = entry.beam_on_sec != null ? entry.beam_on_sec : fullSec
  return { fullMin: fullSec / 60, beamMin: beamSec / 60 }
}

function formatDim(val) {
  if (val == null) return '0'
  return val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)
}

function defaultProductId(fileName) {
  if (!fileName) return ''
  return fileName
    .replace(/\.dxf$/i, '')
    .replace(/\s+/g, '-')
    .toUpperCase()
}

function autoDescription(w, h) {
  if (!w || !h) return ''
  const l = Math.max(w, h)
  const s = Math.min(w, h)
  return `${formatDim(l)} x ${formatDim(s)}`
}

/** Render an SVG string to a PNG Blob via offscreen canvas. */
function svgToPngBlob(svgString, maxSize = 400) {
  return new Promise((resolve, reject) => {
    if (!svgString) return reject(new Error('No SVG data'))

    const img = new Image()
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      try {
        // Scale to fit within maxSize while preserving aspect ratio
        let w = img.naturalWidth || maxSize
        let h = img.naturalHeight || maxSize
        if (w === 0 || h === 0) { w = maxSize; h = maxSize }
        const scale = Math.min(maxSize / w, maxSize / h, 1)
        const cw = Math.round(w * scale)
        const ch = Math.round(h * scale)

        const canvas = document.createElement('canvas')
        canvas.width = cw
        canvas.height = ch
        const ctx = canvas.getContext('2d')
        // White background
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, cw, ch)
        ctx.drawImage(img, 0, 0, cw, ch)

        canvas.toBlob(blob => {
          URL.revokeObjectURL(url)
          if (blob) resolve(blob)
          else reject(new Error('Canvas toBlob returned null'))
        }, 'image/png')
      } catch (err) {
        URL.revokeObjectURL(url)
        reject(err)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG as image'))
    }

    img.src = url
  })
}

const MATERIAL_PRICE_METHODS = [
  { value: 'weight', label: 'By Weight' },
  { value: 'bounding_area', label: 'By Bounding Area' },
  { value: 'solid_profile', label: 'By Solid Profile' },
  { value: 'manual', label: 'Customer Supplied / No Cost' },
]

export default function CreatePartsModal({
  show, onClose, parts, project, nestResult, materialGroups, thicknesses,
  customers, surfaceFinishes,
  projectSurfaceFinish, projectCustomerId, projectMaterialType,
  projectThicknessDisplay, projectThicknessIn, projectFeedrateIpm, projectPierceTimeSec,
}) {
  const initialRows = useMemo(() => {
    return (parts || []).map(part => {
      const group = materialGroups?.find(g => g.group_id === part.material_group_id)
      const matType = group?.material_type || projectMaterialType || ''
      const thickDisp = group?.thickness_display || projectThicknessDisplay || ''
      const thickIn = group?.thickness_in || (projectThicknessIn ? parseFloat(projectThicknessIn) : null)
      const feedrate = group?.feedrate_ipm || (projectFeedrateIpm ? parseFloat(projectFeedrateIpm) : null)
      const pierceSec = group?.pierce_time_sec || (projectPierceTimeSec ? parseFloat(projectPierceTimeSec) : null)
      const sf = group?.surface_finish || projectSurfaceFinish || ''
      const custId = projectCustomerId || ''

      const matchedThickness = thicknesses.find(t =>
        t.display_thickness === thickDisp &&
        t.material_type?.toLowerCase() === matType.toLowerCase()
      )

      const w = part.bounding_width
      const h = part.bounding_height
      const sizeLength = w && h ? Math.max(w, h) : null
      const sizeWidth = w && h ? Math.min(w, h) : null
      const weight = calcWeight(part.part_area_sq_in, thickIn, matType)

      // Prefer the nester's allocated per-part time from the latest nest
      // result (includes proportional share of sheet overhead — SH Cut, Move,
      // Pierce cycle, Nozzle Clean, Alignment, Shuttle, Other). Falls back
      // to bare beam-on when no nest has been run yet.
      const nesterTimes = lookupNesterTimes(part.part_id, nestResult)
      const beamOnMin = nesterTimes
        ? nesterTimes.beamMin
        : calcBeamOnMinutes(part.cut_length_in, part.pierce_count, feedrate, pierceSec)
      const laserMin = nesterTimes ? nesterTimes.fullMin : beamOnMin

      return {
        partId: part.part_id,
        productId: defaultProductId(part.file_name),
        description: autoDescription(w, h),
        sizeLengthIn: sizeLength,
        sizeWidthIn: sizeWidth,
        weightLbs: weight,
        materialType: matType,
        surfaceFinish: sf,
        thicknessId: matchedThickness?.thickness_id || null,
        thicknessDisplay: thickDisp,
        laserMinutes: laserMin,
        beamOnMinutes: beamOnMin,
        pierceCount: part.pierce_count || 0,
        solidProfileArea: part.part_area_sq_in || null,
        customerId: custId,
        selected: true,
        status: 'pending',
        error: null,
        // Keep nester data for file/image upload
        fileName: part.file_name,
        svgData: part.svg_data,
        dxfS3Key: part.dxf_s3_key,
      }
    })
  }, [parts, nestResult, materialGroups, thicknesses, projectMaterialType, projectThicknessDisplay,
      projectThicknessIn, projectFeedrateIpm, projectPierceTimeSec, projectSurfaceFinish, projectCustomerId])

  const [rows, setRows] = useState(initialRows)
  const [creating, setCreating] = useState(false)
  const [pin, setPin] = useState('')
  const [materialPriceMethod, setMaterialPriceMethod] = useState('weight')

  function updateRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  function toggleAll() {
    const allSelected = rows.every(r => r.selected || r.status === 'created')
    setRows(prev => prev.map(r => r.status === 'created' ? r : { ...r, selected: !allSelected }))
  }

  async function createParts() {
    setCreating(true)
    const toCreate = rows.filter(r => r.selected && r.status !== 'created')

    for (let i = 0; i < toCreate.length; i++) {
      const row = toCreate[i]
      const idx = rows.findIndex(r => r.partId === row.partId)
      setRows(prev => prev.map((r, j) => j === idx ? { ...r, status: 'creating' } : r))

      try {
        // 1. Create the part
        const payload = {
          product_id: row.productId,
          description: row.description,
          customer_id: row.customerId ? parseInt(row.customerId) : null,
          stock_type: 'Sheet Stock',
          size_length: row.sizeLengthIn ? parseFloat(row.sizeLengthIn) : null,
          size_width: row.sizeWidthIn ? parseFloat(row.sizeWidthIn) : null,
          weight_lbs: row.weightLbs ? parseFloat(row.weightLbs.toFixed(2)) : null,
          material_type: row.materialType || null,
          surface_finish: row.surfaceFinish || null,
          thickness_id: row.thicknessId ? parseInt(row.thicknessId) : null,
          process_steps: 'Laser',
          initial_est_laser_minutes: row.laserMinutes ? parseFloat(row.laserMinutes.toFixed(2)) : null,
          beam_on_minutes: row.beamOnMinutes ? parseFloat(row.beamOnMinutes.toFixed(2)) : null,
          pierce_count: row.pierceCount || null,
          solid_profile_area: row.solidProfileArea ? parseFloat(row.solidProfileArea.toFixed(4)) : null,
          material_price_method: materialPriceMethod,
          active_status: true,
          is_temporary: true,
        }

        const res = await fetch(`${API}/parts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (!res.ok) {
          const errText = await res.text()
          const isDup = errText.toLowerCase().includes('duplicate') || errText.toLowerCase().includes('already exists')
          throw new Error(isDup ? 'Part already exists' : `Error ${res.status}`)
        }

        const newPart = await res.json()
        const newPartId = newPart.part_id

        // 2. Upload DXF file as a document (best-effort)
        if (row.dxfS3Key && pin) {
          try {
            const dxfRes = await fetch(`${NESTER}/parts/${row.partId}/dxf`)
            if (dxfRes.ok) {
              const dxfBlob = await dxfRes.blob()
              // Over the gateway's payload ceiling the POST is refused with a bare
              // 413 and no CORS header, so the catch below would swallow it as an
              // indistinguishable network blip. Name it instead of guessing.
              const tooBig = checkUploadSize(
                { size: dxfBlob.size, name: row.fileName || 'part.dxf' }, 'DXF')
              if (tooBig) throw new Error(tooBig)
              const docForm = new FormData()
              docForm.append('file', dxfBlob, row.fileName || 'part.dxf')
              docForm.append('display_name', row.fileName || 'part.dxf')
              docForm.append('description', 'DXF file from nester')
              docForm.append('pin', pin)

              await fetch(`${API}/parts/${newPartId}/documents`, {
                method: 'POST',
                body: docForm,
              })
            }
          } catch (e) {
            console.warn('DXF document upload failed (non-blocking):', e)
          }
        }

        // 3. Calculate and set material cost (best-effort)
        if (materialPriceMethod && materialPriceMethod !== 'manual') {
          try {
            const priceRes = await fetch(`${API}/parts/${newPartId}/material_price`)
            if (priceRes.ok) {
              const priceData = await priceRes.json()
              let matCost = 0
              if (materialPriceMethod === 'weight') matCost = priceData.price_by_weight || 0
              else if (materialPriceMethod === 'bounding_area') matCost = priceData.price_by_bounding_area || 0
              else if (materialPriceMethod === 'solid_profile') matCost = priceData.price_by_solid_profile || 0
              if (matCost > 0) {
                await fetch(`${API}/parts/${newPartId}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ material_cost_component: Math.round(matCost * 100) / 100 }),
                })
              }
            }
          } catch (e) {
            console.warn('Material price calc failed (non-blocking):', e)
          }
        }

        // 4. Upload SVG-derived PNG as part image (best-effort)
        if (row.svgData) {
          try {
            const pngBlob = await svgToPngBlob(row.svgData)
            const imgForm = new FormData()
            imgForm.append('file', pngBlob, `${row.productId || 'part'}.png`)

            await fetch(`${API}/parts/${newPartId}/upload-image`, {
              method: 'POST',
              body: imgForm,
            })
          } catch (e) {
            console.warn('Part image upload failed (non-blocking):', e)
          }
        }

        setRows(prev => prev.map((r, j) => j === idx ? { ...r, status: 'created', error: null } : r))
      } catch (err) {
        setRows(prev => prev.map((r, j) => j === idx ? { ...r, status: 'error', error: err.message } : r))
      }
    }
    setCreating(false)
  }

  const selectedCount = rows.filter(r => r.selected && r.status !== 'created').length
  const createdCount = rows.filter(r => r.status === 'created').length

  if (!show) return null

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <h5 style={{ margin: 0, fontSize: '1rem' }}>Create Parts in JobQ</h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div style={s.body}>
          {/* PIN input */}
          <div style={{ padding: '0.5rem 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', color: '#475569' }}>
              Worker PIN:
            </label>
            <input
              type="password"
              className="form-control form-control-sm"
              value={pin}
              onChange={e => setPin(e.target.value)}
              placeholder="Enter PIN for file uploads"
              style={{ maxWidth: 160, fontSize: '0.78rem' }}
              disabled={creating}
            />
            {!pin && (
              <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                Required to attach DXF files
              </span>
            )}
          </div>

          {/* Material Price Method */}
          <div style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', color: '#475569' }}>
              Material Pricing:
            </label>
            {MATERIAL_PRICE_METHODS.map(m => (
              <label key={m.value} style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.78rem', cursor: 'pointer',
                color: materialPriceMethod === m.value ? '#1e40af' : '#64748b',
                fontWeight: materialPriceMethod === m.value ? 600 : 400,
              }}>
                <input type="radio" name="matPriceMethod" value={m.value}
                  checked={materialPriceMethod === m.value}
                  onChange={() => setMaterialPriceMethod(m.value)}
                  disabled={creating} />
                {m.label}
              </label>
            ))}
          </div>

          {rows.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              No parts to create. Upload DXF files first.
            </div>
          ) : (
            <table className="table table-sm" style={{ fontSize: '0.8rem', marginBottom: 0 }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ width: 30 }}>
                    <input type="checkbox"
                      checked={rows.every(r => r.selected || r.status === 'created')}
                      onChange={toggleAll} />
                  </th>
                  <th>Product ID</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>L x W</th>
                  <th style={{ textAlign: 'right' }}>Weight</th>
                  <th style={{ textAlign: 'right' }}>Laser Time</th>
                  <th style={{ width: 80, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.partId} style={{
                    opacity: row.status === 'created' ? 0.6 : 1,
                    backgroundColor: row.status === 'error' ? '#fef2f2' : row.status === 'created' ? '#f0fdf4' : 'transparent',
                  }}>
                    <td>
                      <input type="checkbox" checked={row.selected}
                        disabled={row.status === 'created'}
                        onChange={e => updateRow(idx, 'selected', e.target.checked)} />
                    </td>
                    <td>
                      <input className="form-control form-control-sm" value={row.productId}
                        onChange={e => updateRow(idx, 'productId', e.target.value)}
                        disabled={row.status === 'created'}
                        style={{ fontSize: '0.78rem', padding: '2px 6px', fontWeight: 600 }} />
                    </td>
                    <td>
                      <input className="form-control form-control-sm" value={row.description}
                        onChange={e => updateRow(idx, 'description', e.target.value)}
                        disabled={row.status === 'created'}
                        style={{ fontSize: '0.78rem', padding: '2px 6px' }} />
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                      {row.sizeLengthIn ? `${formatDim(row.sizeLengthIn)} x ${formatDim(row.sizeWidthIn)}` : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: '0.78rem' }}>
                      {row.weightLbs ? `${row.weightLbs.toFixed(1)} lb` : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: '0.78rem' }}>
                      {row.laserMinutes != null ? `${Math.floor(row.laserMinutes)}:${String(Math.round((row.laserMinutes % 1) * 60)).padStart(2, '0')}` : '-'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {row.status === 'pending' && <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>-</span>}
                      {row.status === 'creating' && (
                        <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                      )}
                      {row.status === 'created' && (
                        <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>Created</span>
                      )}
                      {row.status === 'error' && (
                        <span style={{ color: '#dc2626', fontSize: '0.72rem' }} title={row.error}>
                          {row.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={s.footer}>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
            {createdCount > 0 && <span style={{ color: '#16a34a', fontWeight: 600 }}>{createdCount} created</span>}
            {createdCount > 0 && selectedCount > 0 && ' | '}
            {selectedCount > 0 && `${selectedCount} selected`}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-sm btn-primary" onClick={createParts}
              disabled={creating || selectedCount === 0 || !pin}>
              {creating ? 'Creating...' : `Create ${selectedCount} Part${selectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    backgroundColor: 'white', borderRadius: '12px', width: '900px', maxWidth: '95vw',
    maxHeight: '80vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  },
  header: {
    padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  body: {
    flex: 1, overflow: 'auto', padding: '0.5rem',
  },
  footer: {
    padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
}
